import React, { useState } from "react";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  useTheme,
  alpha,
  Card,
  CardContent,
  Tabs,
  Tab,
  Grow,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StarIcon from "@mui/icons-material/Star";

// Interface para as propriedades de cada plano
interface PlanoProps {
  titulo: string;
  subtitulo: string;
  preco: string;
  precoAntigo?: string;
  destaque: boolean;
  beneficios: string[];
  recomendado?: boolean;
}

// Planos por tipo (Individual, Familiar, Empresarial)
const planosPorTipo: { [key: string]: PlanoProps[] } = {
  individual: [
    {
      titulo: "Essencial",
      subtitulo: "Proteção básica para seu dia a dia",
      preco: "R$ 199,90",
      precoAntigo: "R$ 259,90",
      destaque: false,
      beneficios: [
        "Consultas em todas especialidades",
        "Exames básicos",
        "Pronto atendimento 24h",
        "App de telemedicina",
      ],
    },
    {
      titulo: "Premium",
      subtitulo: "Proteção avançada e mais benefícios",
      preco: "R$ 299,90",
      precoAntigo: "R$ 399,90",
      destaque: true,
      recomendado: true,
      beneficios: [
        "Consultas em todas especialidades",
        "Exames completos",
        "Pronto atendimento 24h",
        "App de telemedicina",
        "Cobertura nacional",
        "Desconto em medicamentos",
      ],
    },
    {
      titulo: "Exclusivo",
      subtitulo: "Proteção máxima para sua saúde",
      preco: "R$ 399,90",
      precoAntigo: "R$ 499,90",
      destaque: false,
      beneficios: [
        "Consultas em todas especialidades",
        "Exames completos",
        "Pronto atendimento 24h",
        "App de telemedicina",
        "Cobertura nacional",
        "Desconto em medicamentos",
        "Acomodação em apartamento",
        "Rede premium de hospitais",
      ],
    },
  ],
  familiar: [
    {
      titulo: "Família Básico",
      subtitulo: "Para famílias com orçamento controlado",
      preco: "R$ 499,90",
      precoAntigo: "R$ 599,90",
      destaque: false,
      beneficios: [
        "Consultas em todas especialidades",
        "Exames básicos",
        "Pronto atendimento 24h",
        "App de telemedicina",
        "Até 3 dependentes",
      ],
    },
    {
      titulo: "Família Completo",
      subtitulo: "Proteção ideal para toda a família",
      preco: "R$ 699,90",
      precoAntigo: "R$ 899,90",
      destaque: true,
      recomendado: true,
      beneficios: [
        "Consultas em todas especialidades",
        "Exames completos",
        "Pronto atendimento 24h",
        "App de telemedicina",
        "Até 5 dependentes",
        "Desconto em medicamentos",
        "Descontos progressivos por dependente",
      ],
    },
    {
      titulo: "Família Premium",
      subtitulo: "O máximo de proteção para quem você ama",
      preco: "R$ 999,90",
      precoAntigo: "R$ 1.299,90",
      destaque: false,
      beneficios: [
        "Consultas em todas especialidades",
        "Exames completos",
        "Pronto atendimento 24h",
        "App de telemedicina",
        "Dependentes ilimitados",
        "Desconto em medicamentos",
        "Acomodação em apartamento",
        "Rede premium de hospitais",
      ],
    },
  ],
  empresarial: [
    {
      titulo: "Empresarial Básico",
      subtitulo: "Para pequenas empresas",
      preco: "R$ 149,90",
      precoAntigo: "R$ 199,90",
      destaque: false,
      beneficios: [
        "Consultas em todas especialidades",
        "Exames básicos",
        "Pronto atendimento 24h",
        "A partir de 3 pessoas",
        "Telemedicina corporativa",
      ],
    },
    {
      titulo: "Empresarial Avançado",
      subtitulo: "Para empresas em crescimento",
      preco: "R$ 199,90",
      precoAntigo: "R$ 259,90",
      destaque: true,
      recomendado: true,
      beneficios: [
        "Consultas em todas especialidades",
        "Exames completos",
        "Pronto atendimento 24h",
        "A partir de 3 pessoas",
        "Telemedicina corporativa",
        "Desconto em medicamentos",
        "Medicina ocupacional",
      ],
    },
    {
      titulo: "Empresarial Premium",
      subtitulo: "Para médias e grandes empresas",
      preco: "R$ 249,90",
      precoAntigo: "R$ 329,90",
      destaque: false,
      beneficios: [
        "Consultas em todas especialidades",
        "Exames completos",
        "Pronto atendimento 24h",
        "A partir de 3 pessoas",
        "Telemedicina corporativa",
        "Desconto em medicamentos",
        "Medicina ocupacional",
        "Acomodação em apartamento",
        "Programa de qualidade de vida",
      ],
    },
  ],
};

export const CtaSection = () => {
  const theme = useTheme();
  const [tipoPlano, setTipoPlano] = useState<string>("individual");
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setTipoPlano(newValue);
  };

  return (
    <Box
      id="planos"
      sx={{
        py: { xs: 8, md: 12 },
        background: alpha(theme.palette.primary.light, 0.03),
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Elementos decorativos */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: { xs: 150, md: 300 },
          height: { xs: 150, md: 300 },
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.light,
            0.1
          )} 0%, ${alpha(theme.palette.secondary.light, 0.1)} 100%)`,
          borderBottomLeftRadius: "100%",
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ textAlign: "center", mb: { xs: 5, md: 6 } }}>
          <Typography
            variant="h6"
            component="p"
            sx={{
              fontWeight: 600,
              color: theme.palette.primary.main,
              mb: 2,
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            Nossos Planos
          </Typography>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: "2rem", md: "3rem" },
              color: theme.palette.text.primary,
            }}
          >
            Escolha o plano ideal para você
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: "700px",
              mx: "auto",
              mb: 4,
            }}
          >
            Temos opções para todos os perfis e necessidades, com coberturas
            completas e preços que cabem no seu bolso.
          </Typography>

          {/* Tabs para escolher o tipo de plano */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 5,
              position: "relative",
            }}
          >
            <Box
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                borderRadius: 30,
                p: 0.5,
                width: "fit-content",
              }}
            >
              <Tabs
                value={tipoPlano}
                onChange={handleChange}
                sx={{
                  minHeight: 44,
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                  "& .MuiTab-root": {
                    minHeight: 44,
                    px: 3,
                    py: 1,
                    borderRadius: 30,
                    color: theme.palette.text.primary,
                    fontWeight: 500,
                    fontSize: { xs: "0.85rem", md: "0.95rem" },
                    "&.Mui-selected": {
                      color: "white",
                      fontWeight: 600,
                      bgcolor: theme.palette.primary.main,
                    },
                  },
                }}
              >
                <Tab label="Individual" value="individual" />
                <Tab label="Familiar" value="familiar" />
                <Tab label="Empresarial" value="empresarial" />
              </Tabs>
            </Box>
          </Box>
        </Box>

        {/* Cards de planos */}
        <Grid container spacing={4} justifyContent="center">
          {planosPorTipo[tipoPlano].map((plano, index) => (
            <Grow
              in={true}
              timeout={300 * (index + 1)}
              key={`${tipoPlano}-${index}`}
            >
              <Grid
                item
                xs={12}
                md={4}
                sx={{
                  display: "flex",
                }}
              >
                <Card
                  sx={{
                    width: "100%",
                    borderRadius: 4,
                    position: "relative",
                    overflow: "visible",
                    transition: "all 0.3s ease",
                    transform: plano.destaque ? { md: "scale(1.05)" } : "none",
                    boxShadow: plano.destaque
                      ? `0 20px 30px ${alpha(theme.palette.common.black, 0.1)}`
                      : `0 8px 15px ${alpha(theme.palette.common.black, 0.07)}`,
                    border: plano.destaque
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                    "&:hover": {
                      transform: plano.destaque
                        ? { md: "scale(1.08)" }
                        : { md: "scale(1.03)" },
                      boxShadow: plano.destaque
                        ? `0 25px 35px ${alpha(
                            theme.palette.common.black,
                            0.15
                          )}`
                        : `0 12px 20px ${alpha(
                            theme.palette.common.black,
                            0.1
                          )}`,
                    },
                    ...(isMobile && {
                      mb: plano.destaque ? 6 : 0,
                    }),
                  }}
                >
                  {plano.recomendado && (
                    <Chip
                      label="Mais Popular"
                      icon={<StarIcon />}
                      color="secondary"
                      sx={{
                        position: "absolute",
                        top: -15,
                        right: "50%",
                        transform: "translateX(50%)",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                        height: 30,
                        "& .MuiChip-icon": {
                          fontSize: "1rem",
                        },
                      }}
                    />
                  )}

                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Typography
                      variant="h5"
                      component="h3"
                      sx={{
                        fontWeight: 700,
                        mb: 1,
                        color: plano.destaque
                          ? theme.palette.primary.main
                          : theme.palette.text.primary,
                      }}
                    >
                      {plano.titulo}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mb: 3, color: theme.palette.text.secondary }}
                    >
                      {plano.subtitulo}
                    </Typography>

                    <Box
                      sx={{ display: "flex", alignItems: "baseline", mb: 4 }}
                    >
                      <Typography
                        variant="h3"
                        component="span"
                        sx={{
                          fontWeight: 700,
                          color: plano.destaque
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                        }}
                      >
                        {plano.preco}
                      </Typography>
                      <Typography
                        variant="body1"
                        component="span"
                        sx={{ ml: 1, color: theme.palette.text.secondary }}
                      >
                        /mês
                      </Typography>
                    </Box>

                    {plano.precoAntigo && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 3,
                        }}
                      >
                        <Typography
                          component="span"
                          sx={{
                            textDecoration: "line-through",
                            color: theme.palette.text.secondary,
                            fontWeight: 500,
                          }}
                        >
                          {plano.precoAntigo}/mês
                        </Typography>
                        <Chip
                          label="Economia"
                          size="small"
                          sx={{
                            backgroundColor: alpha(
                              theme.palette.secondary.main,
                              0.1
                            ),
                            color: theme.palette.secondary.main,
                            fontWeight: 600,
                            fontSize: "0.7rem",
                          }}
                        />
                      </Box>
                    )}

                    <Divider sx={{ mb: 3 }} />

                    <List dense sx={{ mb: 3 }}>
                      {plano.beneficios.map((beneficio, idx) => (
                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon
                              sx={{
                                color: plano.destaque
                                  ? theme.palette.primary.main
                                  : theme.palette.secondary.main,
                                fontSize: 20,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={beneficio}
                            primaryTypographyProps={{
                              fontWeight: 500,
                              variant: "body2",
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>

                    <Button
                      variant={plano.destaque ? "contained" : "outlined"}
                      color={plano.destaque ? "primary" : "primary"}
                      fullWidth
                      size="large"
                      href="#formulario"
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        py: 1.5,
                        borderRadius: 5,
                        fontWeight: 600,
                        ...(plano.destaque && {
                          background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        }),
                      }}
                    >
                      {plano.destaque
                        ? "Quero este plano"
                        : "Solicitar cotação"}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grow>
          ))}
        </Grid>

        <Box sx={{ textAlign: "center", mt: 6 }}>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, mb: 2 }}
          >
            Não encontrou o que procura? Temos opções personalizadas para você.
          </Typography>
          <Button
            variant="text"
            color="primary"
            href="#formulario"
            sx={{ fontWeight: 600 }}
          >
            Fale com um especialista
          </Button>
        </Box>
      </Container>
    </Box>
  );
};
